import React, {useMemo} from 'react'
import styled from 'styled-components'
import {IMAGE_ASSET} from 'consts'
import {useTranslation} from 'i18n'
import {StoreType} from 'types'
import {getBorder, getWindowPlatform} from 'utils'
import {Button, Icon, Image, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertUnit(4)};
  text-align: left;
  padding: ${convertUnit(12)} ${convertUnit(20)};
  border-radius: ${convertUnit(8)};
  border: ${({theme}) => getBorder(convertUnit(1), 'solid', theme.gray_1)};
  background-color: ${({theme}) => theme.white_3};
  box-sizing: border-box;
`

const StyledListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-left: ${convertUnit(4)};
`

const StyledListItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(5)};
  justify-content: flex-start;
  align-items: flex-start;
  line-height: ${convertUnit(21)};
`

const StyledSupportContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const StyledIconContainer = styled.div`
  aspect-ratio: 1;
  padding: ${convertUnit(5)};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledLink = styled.a`
  text-decoration: none;
`

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(12)};
`

const StyledButton = styled(Button)`
  margin-top: ${convertUnit(16)};
`

const StyledStoreImagesContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledImg = styled(Image)`
  height: ${convertUnit(35)};
  width: ${convertUnit(100)};
  object-fit: contain;
  cursor: pointer;
`

const StyledWebCheckoutContainer = styled.div`
  display: flex;
  flex-direction: column;
`

interface PaymentCheckoutStatusSuccessWaitingHiresInfoProps {
  hasPackage: boolean
  onClick(): void
  onClickStore(type: StoreType): void
}
export default function PaymentCheckoutStatusSuccessWaitingHiresInfo({
  hasPackage,
  onClick,
  onClickStore,
}: PaymentCheckoutStatusSuccessWaitingHiresInfoProps) {
  const {translate} = useTranslation()
  const isWeb = getWindowPlatform() === 'web'
  const googlePlayImg = IMAGE_ASSET('giftshop', 'google-play-icon.png')
  const appStoreImg = IMAGE_ASSET('giftshop', 'app-store-icon.png')

  const handleRenderTitle = useMemo(
    () => (
      <StyledTitleContainer>
        <Icon type="collection" color="primary_5" />
        <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
          {translate('payment:checkoutWaitForHiresInfoTitle', {
            context: hasPackage
              ? isWeb
                ? 'package_web'
                : 'package'
              : undefined,
          })}
        </Paragraph>
      </StyledTitleContainer>
    ),
    [hasPackage, isWeb, translate],
  )

  const handleRenderItems = useMemo(
    () => (
      <StyledListContainer>
        {Array.from({length: 3}).map((_, index) => (
          <StyledListItem>
            <Paragraph
              color="gray_6"
              fontWeight={hasPackage ? 'regular' : 'bold'}>
              {hasPackage ? `${index + 1}.` : `•`}
            </Paragraph>
            <Paragraph color="gray_6">
              {translate(
                hasPackage
                  ? 'payment:checkoutWaitForHiresInfoDescPackage'
                  : 'payment:checkoutWaitForHiresInfoDesc',
                {context: `${index}`},
              )}
            </Paragraph>
          </StyledListItem>
        ))}
      </StyledListContainer>
    ),
    [hasPackage, translate],
  )

  const handleRenderSupport = useMemo(
    () => (
      <StyledSupportContainer>
        <Paragraph fontSize="xs" color="gray_5">
          {translate('global:fotoyuSupportTeam')}
        </Paragraph>
        <StyledIconContainer>
          <StyledLink href="https://wa.me/6281219000813" target="_blank">
            <Icon type="whatsapp-ol" color="gray_5" size={convertUnit(18)} />
          </StyledLink>
        </StyledIconContainer>
        <StyledIconContainer>
          <StyledLink
            href="https://www.instagram.com/fotoyu_official/"
            target="_blank">
            <Icon type="instagram-ol" color="gray_5" size={convertUnit(18)} />
          </StyledLink>
        </StyledIconContainer>
      </StyledSupportContainer>
    ),
    [translate],
  )

  const handleRenderButton = useMemo(
    () =>
      hasPackage && isWeb ? (
        <StyledWebCheckoutContainer>
          <StyledStoreImagesContainer>
            <StyledImg
              src={googlePlayImg}
              alt="google-play"
              onClick={() => onClickStore('play-store')}
            />
            <StyledImg
              src={appStoreImg}
              alt="app-store"
              onClick={() => onClickStore('app-store')}
            />
          </StyledStoreImagesContainer>
          <StyledButton
            label={translate('payment:buttonOpenCollection')}
            onClick={onClick}
          />
        </StyledWebCheckoutContainer>
      ) : (
        <StyledButton
          label={translate(
            hasPackage
              ? 'payment:buttonOpenActivePackage'
              : 'payment:buttonOpenCollection',
          )}
          onClick={onClick}
        />
      ),
    [
      appStoreImg,
      googlePlayImg,
      hasPackage,
      isWeb,
      onClick,
      onClickStore,
      translate,
    ],
  )

  return (
    <>
      <StyledContainer>
        {handleRenderTitle}
        {handleRenderItems}
        {handleRenderButton}
      </StyledContainer>
      {handleRenderSupport}
    </>
  )
}
